// hooks/useItemActions.js
import { toRefs, computed, reactive, ref, inject } from 'vue'
import { router } from '@inertiajs/vue3'
import { useStore } from 'vuex'
import _ from 'lodash-es'
import { propsFactory } from 'vuetify/lib/util/index.mjs' // Types

import ACTIONS from '@/store/actions'
import { ALERT } from '@/store/mutations/index'
import api from '@/store/api/form'

import { useConfig, useCastAttributes, useDynamicModal } from '@/hooks'
import { checkItemConditions } from '@/utils/itemConditions'
import { isset, isObject } from '@/utils/helpers'

export const makeItemActionsProps = propsFactory({
  isEditing: {
    type: Boolean,
    default: false
  },
  actions: {
    type: [Array, Object],
    default: () => []
  }
})

export default function useItemActions(props, context) {
  const store = useStore()
  const { shouldUseInertia } = useConfig()
  const { castObjectAttributes } = useCastAttributes()
  const dynamicModal = useDynamicModal()

  const pageLoading = inject('pageLoadingOverlay', null)

  const Actions = _.cloneDeep(props.actions)

  const editingItem = context.actionItem
    || context.item
    || context.editedItem
    || props.item
    || props.editedItem

  const editedModel = context.editedModel ?? null

  const resolveParamValue = (config) => {
    if (!config.source || !config.find || !config.return) {
      return config;
    }

    const sourceData = editingItem[config.source];
    if (!Array.isArray(sourceData)) {
      return undefined;
    }

    const [findKey, findValue] = config.find;
    const item = sourceData.find(item => item[findKey] === findValue);

    return item ? item[config.return] : undefined;
  }

  const handleRequestAction = (action, endpoint) => {
    if (!endpoint) {
      console.error('Endpoint not specified for request action');
      return;
    }

    const method = action.method?.toLowerCase() || 'post';
    if (!api[method]) {
      console.error('Invalid request method:', method);
      return;
    }

    // Prepare parameters
    const params = {};

    // Process each parameter based on its configuration
    for (const [key, config] of Object.entries(action.params)) {
      if (typeof config === 'object' && config !== null) {
        const value = resolveParamValue(config);
        if (value === undefined) {
          console.error(`Could not resolve parameter value for ${key}`);
          return;
        }
        params[key] = value;
      } else {
        params[key] = config;
      }
    }

    if (action.includeFormData && editedModel) {
      const fields = Array.isArray(action.includeFormData) ? action.includeFormData : Object.keys(editedModel)
      fields.forEach(field => {
        if (editedModel[field] !== undefined) {
          params[field] = editedModel[field]
        }
      })
    }
    pageLoading?.show();

    api[method](endpoint, params,
      (response) => {
        const showAlert = () => {
          if (!response.data.message) {
            return;
          }

          let actionResponseMessage = action.responseMessage || {};
          let message = response.data.message;
          let variant = response.data.variant;

          if(_.isString(actionResponseMessage)) {
            actionResponseMessage = {
              success: actionResponseMessage,
              error: actionResponseMessage
            }
          }

          if(isset(actionResponseMessage[variant])) {
            message = actionResponseMessage[variant];
          }

          store.commit(ALERT.SET_ALERT, {
            message: message,
            variant: variant
          });
        }

        context.emit('actionComplete', { action, response });

        // Reload the page after successful operation
        if (action.reloadOnSuccess === true) {
          const forceRefresh = action.forceRefresh || false
          if(shouldUseInertia.value && !forceRefresh) {
            router.reload({
              only: ['formAttributes', ...(action.reloadOnly || [])],
              onFinish: () => {
                pageLoading?.hide();
                showAlert();
              }
            })
          } else {
            setTimeout(() => {
              window.location.reload()
            }, action.reloadDelay || 1000); // 1 second delay to show the success message
          }
        } else {
          pageLoading?.hide();
          showAlert();
        }
      },
      (error) => {
        pageLoading?.hide();
        store.commit(ALERT.SET_ALERT, {
          message: error.data?.message || 'Action failed',
          variant: 'error'
        });
      }
    );
  }

  const handleModalAction = (action, endpoint) => {
    // Assuming you have a modal system
    store.commit('SET_MODAL', {
      show: true,
      title: action.label,
      component: 'ue-form',
      props: {
        schema: action.schema,
        actionUrl: endpoint,
        async: true
      },
      on: {
        success: (response) => {
          store.commit(ALERT.SET_ALERT, {
            message: response.message || 'Action completed successfully',
            variant: 'success'
          });
          context.emit('action-complete', { action, response });
        }
      }
    });
  }

  const handleBlankAction = (endpoint) => {
    if (!endpoint) {
      console.error('Endpoint not specified for blank action');
      return;
    }

    window.open(endpoint, '_blank');
  }

  const handleDownloadAction = (endpoint) => {
    if (!endpoint) {
      console.error('Endpoint not specified for download action');
      return;
    }

    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = endpoint;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const validateAction = (action) => {
    // Base condition for editing/creating
    if(!editingItem) {
      return true
    }

    const baseCondition = props.isEditing ? action.editable : action.creatable;

    // If no conditions defined, return base condition
    if (!action.conditions || !baseCondition) {
      return baseCondition;
    }

    // Check all conditions
    return baseCondition
      && checkItemConditions(action.conditions, editingItem)
      && checkItemConditions(action.userConditions ?? [], store.getters.userProfile);
  }

  const formatActions = (_actions) => {
    return _actions.map(action => {

      if(props.isEditing && editingItem){
        action = castObjectAttributes(action, editingItem)
      }

      action.disabled = !validateAction(action)
        || (props.formDirty === true && action.disableOnDirty === true)

      return action
    })
  }

  const flattenedActions = ref(isObject(Actions)
      ? Object.values(Actions)
      : Actions
  )

  const filteredActions = computed(() => flattenedActions.value.filter(action => {
    if(!validateAction(action) ) {
      if(action.hideOnCondition) {
        return false
      }
    }
    return true
  }))

  const allActions = computed(() => formatActions(filteredActions.value))
  const visibleActions = computed(() => allActions.value.filter(action => !(isset(action.disabled) && action.disabled)))

  const states = reactive({
    hasActions: computed(() => allActions.value.length > 0),
    hasVisibleActions: computed(() => visibleActions.value.length > 0),
    allActions,
    visibleActions,
  })

  const methods = reactive({
    // methods
    shouldShowAction: validateAction,
    handleAction(action) {
      let needToConfirm = false;
      let confirmCallback = null;

      if (!action.type) {
        console.warn('Action type not specified:', action);
        return;
      }

      if(isset(action.hasConfirmation) && action.hasConfirmation === true) {
        needToConfirm = true;
      }

      // Replace any URL parameters
      const endpoint = action.endpoint?.replace(':id', editingItem.id);

      switch (action.type) {
        case 'request':
          if(needToConfirm) {
            confirmCallback = async () => {
              handleRequestAction(action, endpoint);

              return true;
            }
          } else {
            handleRequestAction(action, endpoint);
          }
        break;
        case 'modal':
          handleModalAction(action, endpoint);
        break;
        case 'download':
          handleDownloadAction(endpoint);
        break;
        case 'blank':
          if(needToConfirm) {
            confirmCallback = async () => {
              handleBlankAction(endpoint);

              return true;
            }
          } else {
            handleBlankAction(endpoint);
          }
        break;
        default:
          console.warn('Unknown action type:', action.type);
      }

      if(needToConfirm) {
        dynamicModal.open(null, {

          modalProps: {
            ...action.confirmationModalAttributes ?? {},
            confirmCallback: confirmCallback,
          }
        })
      }
    }
  })

  return {
    ...toRefs(states),
    ...toRefs(methods),
    // ...toRefs(states)
  }
}
